'use client';

import get from 'lodash/get';
import { BASE_FILE_URL } from '@/common/constants';
import { CloseOutlined, DownloadOutlined, LeftOutlined, LinkOutlined, PlayCircleOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslations } from 'next-intl';
import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

function checkTypeImg(type, data, level = 0, originUrl) {
  const levels = ['thumbnail', 'small', 'medium', 'large', 'origin'];
  if (!originUrl) return '/img-default.png';
  let currentLevel;
  if (type === 'mobile') currentLevel = 'small';
  else if (type === 'tablet') currentLevel = 'medium';
  else if (type === 'laptop') currentLevel = 'large';
  else if (type === 'desktop') currentLevel = 'large';
  else currentLevel = 'origin';
  let idx = levels.indexOf(currentLevel);
  idx = Math.max(0, Math.min(levels.length - 1, idx + level));
  let url = null;
  for (let i = idx; i < levels.length; i++) {
    url = data?.[levels[i]];
    if (url) break;
  }
  if (!url) url = originUrl;
  return `${BASE_FILE_URL}/${url}`;
}

const getMediaType = (item, urlIndex = 0) => {
  if (!item) return 'unknown';
  if (item.type === 'longforms') return 'longforms';
  if (item.type === 'infographics') return 'infographics';
  if (item.isLink && item.link) {
    if (item.typeLink === 'youtube' || item.typeLink === 'facebook') return 'video';
    else if (item.typeLink) {
      const extension = item.link.split('.').pop().toLowerCase().split('?')[0];
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) return 'video';
      else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
      else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) return 'audio';
      return 'link';
    }
    return 'link';
  }
  if (!item.fileUrls || item.fileUrls.length === 0) return 'unknown';
  const fileUrl = item.fileUrls[urlIndex] || item.fileUrls[0];
  const extension = fileUrl.split('.').pop().toLowerCase();
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) return 'video';
  else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
  else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) return 'audio';
  return 'unknown';
};

const getMediaUrl = (item, index = 0) => {
  if (item.isLink && item.link) return item.link;
  if (!item.fileUrls || item.fileUrls.length === 0) return null;
  return `${item.fileUrls[index] || item.fileUrls[0]}`;
};

// Strip HTML tags, chỉ render text — tránh lỗi Trusted Types
function SafeHTML({ html, className, as: Tag = 'span' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && html) {
      const tmp = document.createElement('div');
      tmp.textContent = html; // không dùng innerHTML
      // Nếu html là plain text thì dùng trực tiếp, nếu là html thì strip tags
      const isHTML = /<[a-z][\s\S]*>/i.test(html);
      if (isHTML) {
        // Dùng DOMParser để parse an toàn, lấy textContent
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          ref.current.textContent = doc.body.textContent || '';
        } catch {
          ref.current.textContent = html.replace(/<[^>]*>/g, '');
        }
      } else {
        ref.current.textContent = html;
      }
    }
  }, [html]);
  return <Tag ref={ref} className={className} />;
}

const getYouTubeVideoId = (url) => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1] : '';
};

const HomeMedia = ({ homeMedias, websiteCode, deviceType }) => {
  const t = useTranslations('');
  const [showPopup, setShowPopup] = useState(false);
  const [animatePopup, setAnimatePopup] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleViewModal = (item) => {
    setSelectedMedia(item);
    setShowPopup(true);
    setTimeout(() => setAnimatePopup(true), 10);
    setCurrentMediaIndex(0);
  };

  const handleNextMedia = () => {
    if (!selectedMedia) return;
    const maxIndex = selectedMedia.isLink ? 0 : (selectedMedia.fileUrls?.length || 1) - 1;
    setCurrentMediaIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  const handlePrevMedia = () => {
    if (!selectedMedia) return;
    const maxIndex = selectedMedia.isLink ? 0 : (selectedMedia.fileUrls?.length || 1) - 1;
    setCurrentMediaIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };

  const closePopup = () => {
    setAnimatePopup(false);
    setTimeout(() => setShowPopup(false), 300);
  };

  const renderPopupContent = () => {
    if (!selectedMedia) return null;
    const mediaType = getMediaType(selectedMedia, currentMediaIndex);
    const mediaUrl = getMediaUrl(selectedMedia, currentMediaIndex);
    const totalFiles = selectedMedia.isLink ? 1 : selectedMedia.fileUrls?.length || 1;
    const avatarUrl = checkTypeImg(deviceType, selectedMedia?.sharpFileUrls?.filter((data) => data?.origin === mediaUrl)?.[0], -1, mediaUrl);

    switch (mediaType) {
      case 'video':
        if (selectedMedia.isLink && selectedMedia.typeLink === 'youtube') {
          const videoId = getYouTubeVideoId(selectedMedia.link);
          return (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        if (selectedMedia.isLink && selectedMedia.typeLink === 'facebook') {
          return (
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-8 rounded-2xl text-center">
              <PlayCircleOutlined className="text-6xl text-white mb-6" />
              <h3 className="text-2xl font-bold text-white mb-6">Facebook Video</h3>
              <a
                href={selectedMedia.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300">
                <span>Xem trên Facebook</span>
                <LinkOutlined />
              </a>
            </div>
          );
        }
        return (
          <div className="relative">
            <video src={`${BASE_FILE_URL}/${mediaUrl}`} controls autoPlay className="max-w-full max-h-[70vh] rounded-lg" />
            {totalFiles > 1 && (
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                File {currentMediaIndex + 1} / {totalFiles}
              </div>
            )}
          </div>
        );

      case 'image':
        return (
          <div className="relative flex justify-center">
            <img src={avatarUrl} alt={selectedMedia?.name} className="max-w-full max-h-[80vh] rounded-lg" />
            {totalFiles > 1 && (
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                File {currentMediaIndex + 1} / {totalFiles}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-white text-center p-8">
            <p>Không thể hiển thị media này</p>
          </div>
        );
    }
  };

  const allItems = get(homeMedias, 'results', []);
  const items = allItems
    .filter((item) => {
      const type = getMediaType(item);
      if (activeTab === 0) return type !== 'video';
      if (activeTab === 1) return type === 'video';
      return true;
    })
    .slice(0, 5);
  return (
    <div className="bg-white w-full relative mt-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <a
          href={`${websiteCode ? `/${websiteCode}` : ''}/da-phuong-tien/video`}
          className="text-[15px] font-bold uppercase text-gray-800 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#b71c1c"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
          {t('home.titleContentMedia')}
        </a>

        {/* Tabs */}
        <div className="flex gap-2">
          {['Hình ảnh', 'Video'].map((tab, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`px-4 py-1.5 text-[13px] rounded-full border transition-colors duration-200 font-medium cursor-pointer ${
                activeTab === i
                  ? 'bg-[#b71c1c] text-white border-[#b71c1c]'
                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-[#b71c1c] hover:text-[#b71c1c]'
              }`}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-3 grid-rows-2 gap-2">
        {items.map((item, index) => {
          const mediaUrl = getMediaUrl(item);
          const mediaType = getMediaType(item);
          const avatarUrl = checkTypeImg(deviceType, item?.sharpFileUrls?.filter((d) => d?.origin === mediaUrl)?.[0], -1, mediaUrl);

          return (
            <div
              key={item.id}
              onClick={() => handleViewModal(item)}
              className={`relative overflow-hidden rounded cursor-pointer group ${index === 0 ? 'col-span-1 row-span-2' : 'aspect-[4/3]'}`}>
              {mediaType === 'video' ? (
                <div className="w-full h-[182px] bg-gradient-to-br from-red-400 to-red-700 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
              ) : (
                <Image
                  src={avatarUrl}
                  alt={item.title || item.name || ''}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-300 position-initial"
                />
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-80">
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <SafeHTML html={item.title || item.name || ''} className="text-white text-[12px] font-medium line-clamp-2 leading-snug block" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Popup */}
      {showPopup && (
        <div
          className={`fixed inset-0 z-50 p-4 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
            animatePopup ? 'opacity-100' : 'opacity-0'
          }`}>
          <div
            className={`relative max-w-6xl w-full transform transition-all duration-300 ${
              animatePopup ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'
            }`}>
            <button
              onClick={closePopup}
              className="absolute -top-6 right-[-22px] w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex cursor-pointer items-center justify-center text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:rotate-90 z-10">
              <CloseOutlined className="text-xl" />
            </button>

            {selectedMedia?.fileUrls?.length > 1 && getMediaType(selectedMedia) !== 'audio' && (
              <>
                <button
                  onClick={handlePrevMedia}
                  className="fixed -left-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 z-50 cursor-pointer">
                  <LeftOutlined className="text-lg sm:text-xl" />
                </button>
                <button
                  onClick={handleNextMedia}
                  className="fixed -right-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 z-50 cursor-pointer">
                  <RightOutlined className="text-lg sm:text-xl" />
                </button>
              </>
            )}

            <div className="bg-black/30 backdrop-blur-lg rounded-3xl p-6 border border-white/10 shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="flex flex-col">
                <SafeHTML html={selectedMedia?.name || ''} as="h3" className="text-2xl sm:text-3xl font-bold text-white mb-2" />
                <SafeHTML html={selectedMedia?.content || ''} as="p" className="text-lg text-gray-300 mb-6 leading-relaxed" />

                <div className="mb-6">{renderPopupContent()}</div>

                {selectedMedia?.type !== 'longforms' && (
                  <div className="flex flex-wrap gap-4">
                    {!selectedMedia?.isLink && (
                      <a
                        href={`${BASE_FILE_URL}/${getMediaUrl(selectedMedia, currentMediaIndex)}`}
                        download
                        className="flex items-center space-x-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
                        <DownloadOutlined />
                        <span>{t('media.download')}</span>
                      </a>
                    )}
                    {selectedMedia?.isLink && (
                      <a
                        href={selectedMedia.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
                        <LinkOutlined />
                        <span>Mở liên kết</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeMedia;
